import { useMcpTool } from 'webmcp-react'
import {
  searchHelpCenterSchema,
  readHelpArticleSchema,
  openHelpArticleSchema,
  openSupportMessengerSchema,
} from 'entities/WebMcp/model/schemas'
import { searchHelpCenter, readHelpArticle } from 'entities/WebMcp/lib/helpClient'
import { openHelpArticleView } from 'entities/WebMcp/lib/openHelpArticle'
import { handoffToMessenger } from 'entities/WebMcp/lib/handoffToMessenger'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'

/**
 * The citation rule travels with the articles it governs, rather than sitting in
 * the system prompt: it is about these urls, and it arrives next to them. A bare
 * article title is not a citation, and the model must never assemble a url, so the
 * rule has to be read at the moment a real url is in hand.
 */
const CITATION_RULE =
  'End any answer that used this content with a final line starting "Source: " followed by markdown links, using the exact url in the data below. Written out it looks like: Source: [Compliance Master Article](https://help.salesmessage.com/en/articles/11511272-compliance-master-article). A bare title with no link does not count, and you must never assemble, shorten, or guess a url. Cite only the one to three articles your answer actually drew on, most relevant first, not every result. If you have no url to cite you do not have a grounded answer: say so and offer the support team instead.'

// The descriptions carry the source-of-truth precedence, per
// specs/006-help-center-access/research.md (R9).
export const HelpTools = () => {
  useMcpTool({
    name: 'search_help_center',
    description:
      'Search the Salesmessage help center for product how-to and concept articles. Use it to EXPLAIN the product: when the user asks what a feature is, why it exists, or how it works — including when an action tool for that feature also exists — and when you need a product fact to act. Do not use it in place of acting: when the user wants a task done and a tool can do it, use that tool instead of describing the steps. Returns a short ranked list; answer from these results and always cite the article link. Never invent steps.',
    input: searchHelpCenterSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ query }) => {
      try {
        return okResult(await searchHelpCenter(query), CITATION_RULE)
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'read_help_article',
    description:
      'Read the full text of one help article by id (from search_help_center). Use only when the search snippet is not enough to answer or to act. Summarize the relevant part; do not reproduce the whole article.',
    input: readHelpArticleSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ article_id }) => {
      try {
        return okResult(await readHelpArticle(article_id), CITATION_RULE)
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'open_help_article',
    description:
      'Open a help article for the user to read the full source — in the in-app help center when available, otherwise in a new browser tab. Offer this after summarizing when the user may want the complete article. Pass the article id and its url from search_help_center.',
    input: openHelpArticleSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ article_id, url }) => {
      const result = openHelpArticleView(article_id, url)
      if (!result.opened) {
        return errorResult(new Error('Could not open the article. Try the article link instead.'))
      }
      return okResult({ ...result, article_id })
    },
  })

  useMcpTool({
    name: 'open_support_messenger',
    description:
      "Open the support messenger so the user can reach the team. Use when the request is pure explanation you cannot ground, needs a human, or the help center returned nothing relevant. Optionally pass the user's question as prefill. This only opens a pre-filled composer — it never sends.",
    input: openSupportMessengerSchema,
    annotations: { readOnlyHint: true },
    handler: async ({ prefill }) => {
      // Closes the agent panel as it opens — the messenger sits below it (SWC-19767).
      if (!handoffToMessenger(prefill)) {
        return errorResult(new Error('The support messenger is not available right now.'))
      }
      return okResult({ opened: true, prefilled: Boolean(prefill?.trim()) })
    },
  })

  return null
}
